import {
  HeartIcon,
  MagnifyingGlassIcon,
  UserCircleIcon,
  ChatBubbleBottomCenterTextIcon,
} from "@heroicons/react/24/outline";
import useWindowSize from "hooks/useWindowResize";
import React, { useEffect, useMemo, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { PathName } from "routers/types";
import MenuBar from "shared/MenuBar/MenuBar";
import { useSelector } from "react-redux";
import { RootState } from "redux/store";

// Module-level scroll baseline. Reset to 0 on every route change
// so the hide/show logic starts fresh after navigation.
let WIN_PREV_POSITION = 0;

interface NavItem {
  name: string;
  link?: PathName;
  icon: any;
}

const FooterNav = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  // Track hide state with a ref so we never rely on getBoundingClientRect()
  // or window.innerHeight — both can return stale values during the mobile
  // keyboard close animation, causing isInViewport false-negatives that
  // immediately hide the nav on the very first scroll of the new page.
  const isNavVisible = useRef(true);
  const windowSize = useWindowSize();
  const WIN_WIDTH = useMemo(() => windowSize.width, [windowSize.width]);
  const location = useLocation();

  const { authenticated } = useSelector((state: RootState) => state.auth);
  const unreadChatCount = useSelector((state: RootState) => state.user.unreadChatCount);

  const NAV: NavItem[] = [
    {
      name: "Home",
      link: "/",
      icon: MagnifyingGlassIcon,
    },
    {
      name: "Favorite",
      link: "/favorite-listings",
      icon: HeartIcon,
    },
    {
      name: "Messages",
      link: "/messages" as PathName,
      icon: ChatBubbleBottomCenterTextIcon,
    },
    {
      name: authenticated ? "Account" : "Log in",
      link: authenticated ? "/account" : "/login",
      icon: UserCircleIcon,
    },
    {
      name: "Menu",
      icon: MenuBar,
    },
  ];

  useEffect(() => {
    window.addEventListener("scroll", handleEvent);
    return () => {
      window.removeEventListener("scroll", handleEvent);
    };
  }, []);

  // Reset FooterNav visibility on every route change.
  // WIN_PREV_POSITION is set to 0 (not window.pageYOffset) because
  // ScrollToTop's window.scrollTo(0,0) is async — reading pageYOffset
  // here would capture the stale pre-navigation value.
  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.classList.remove("FooterNav--hide");
      isNavVisible.current = true;
      WIN_PREV_POSITION = 0;
    }
  }, [location.pathname]);

  const handleEvent = () => {
    window.requestAnimationFrame(showHideHeaderMenu);
  };

  const showHideHeaderMenu = () => {
    let currentScrollPos = window.pageYOffset;
    if (!containerRef.current) return;

    if (currentScrollPos > WIN_PREV_POSITION) {
      // Scrolling DOWN — hide after 80px grace period (only when visible)
      if (isNavVisible.current && currentScrollPos - WIN_PREV_POSITION < 80) {
        return;
      }
      containerRef.current.classList.add("FooterNav--hide");
      isNavVisible.current = false;
    } else {
      // Scrolling UP — show after 80px upward movement (only when hidden)
      if (!isNavVisible.current && WIN_PREV_POSITION - currentScrollPos < 80) {
        return;
      }
      containerRef.current.classList.remove("FooterNav--hide");
      isNavVisible.current = true;
    }

    WIN_PREV_POSITION = currentScrollPos;
  };

  if (WIN_WIDTH > 768) return null;

  return (
    <div
      ref={containerRef}
      className="FooterNav bg-white dark:bg-neutral-800 fixed top-auto bottom-0 inset-x-0 z-30 border-t border-neutral-300 dark:border-neutral-700 transition-transform duration-300 ease-in-out"
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
    >
      <div className="p-2 w-full max-w-lg flex justify-around mx-auto text-sm text-center">
        {NAV.map((item, index) => {
          const active = location.pathname === item.link;
          const isMessages = item.link === "/messages";
          return item.link ? (
            <Link
              key={index}
              to={item.link}
              className={`relative flex flex-col items-center justify-between text-neutral-500 dark:text-neutral-300/90 ${
                active ? "text-neutral-900 dark:text-neutral-100" : ""
              }`}
            >
              <item.icon
                className={`w-6 h-6 ${active ? "text-[#F56948]" : ""}`}
              />
              {/* Unread badge on Messages */}
              {isMessages && unreadChatCount > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-0.5 flex items-center justify-center bg-red-500 text-white text-[10px] font-bold rounded-full">
                  {unreadChatCount > 9 ? "9+" : unreadChatCount}
                </span>
              )}
              <span className="text-[11px] leading-none mt-1">{item.name}</span>
            </Link>
          ) : (
            <div
              key={index}
              className="flex flex-col items-center justify-between text-neutral-500 dark:text-neutral-300/90"
            >
              <item.icon iconClassName="w-6 h-6" className={``} />
              <span className="text-[11px] leading-none mt-1">{item.name}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default FooterNav;
